import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { EntityTile } from '@tailwindComponents/EntityTile';
import { useAuth } from '@context';
import { useAtomValue } from 'jotai';
import { UserRoleService } from '@services';
import { Button } from '@tailwindComponents/Button';
import {
  ProductGroupListing,
  productGroupName,
  productThumbnailSrc,
} from '@views/SKUMaster/components/productGroups';
import { skuOwnThumbnailSrc } from '@views/SKUMaster/components/skuHelpers';
import { SkuInfoModal } from '@tailwindComponents/SkuInfoModal';
import { InventorySKUListing } from './types';
import { ChooseSkuModal } from './ChooseSkuModal';
import {
  despatchTargetOfProduct,
  despatchUnitOf,
  skusToChooseFrom,
} from './productDespatch';
import {
  displayedLostProducts,
  displayedSurplusProducts,
} from '../../../utils/stockDisplay';
import { businessProfileAtom } from '../../../store/jotai';

export type ProductStockTotals = {
  inTransitProducts: number;
  availableProducts: number;
  allocatedProducts: number;
  outForDeliveryProducts: number;
  blockedProducts: number;
  totalProducts: number;
  looseProducts: number;
  lostProducts: number;
  surplusProducts: number;
};

export const productStockTotals = (
  variants: InventorySKUListing[],
  warehouseId: string | null | undefined
): ProductStockTotals => {
  const totals: ProductStockTotals = {
    inTransitProducts: 0,
    availableProducts: 0,
    allocatedProducts: 0,
    outForDeliveryProducts: 0,
    blockedProducts: 0,
    totalProducts: 0,
    looseProducts: 0,
    lostProducts: 0,
    surplusProducts: 0,
  };
  variants.forEach((variant) => {
    (variant.stockByWarehouse ?? []).forEach((stock) => {
      const isOutsideWarehouseFilter =
        warehouseId &&
        warehouseId !== 'All' &&
        stock.warehouseId !== warehouseId;
      if (isOutsideWarehouseFilter) return;
      totals.inTransitProducts += stock.inInboundTransitProducts ?? 0;
      totals.availableProducts +=
        (stock.availableProducts ?? 0) + (stock.availableLooseProducts ?? 0);
      totals.allocatedProducts +=
        (stock.allocatedProducts ?? 0) + (stock.allocatedLooseProducts ?? 0);
      totals.outForDeliveryProducts +=
        (stock.inOutboundTransitProducts ?? 0) +
        (stock.inOutboundTransitLooseProducts ?? 0);
      totals.blockedProducts += stock.blockedProducts ?? 0;
      totals.totalProducts +=
        (stock.totalProducts ?? 0) +
        (stock.availableLooseProducts ?? 0) +
        (stock.allocatedLooseProducts ?? 0) +
        (stock.inOutboundTransitLooseProducts ?? 0);
      totals.looseProducts +=
        (stock.availableLooseProducts ?? 0) +
        (stock.allocatedLooseProducts ?? 0) +
        (stock.inOutboundTransitLooseProducts ?? 0);
    });
    totals.lostProducts += displayedLostProducts(
      variant.stockByWarehouse,
      warehouseId
    );
    totals.surplusProducts += displayedSurplusProducts(
      variant.stockByWarehouse,
      warehouseId
    );
  });
  return totals;
};

export function ProductStockRow({
  group,
  warehouseId,
  showOwner = false,
}: {
  group: ProductGroupListing;
  warehouseId: string | null | undefined;
  showOwner?: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const businessProfile = useAtomValue(businessProfileAtom);
  const canCreateDespatchNote =
    businessProfile?.hasConnectedBankAccount ||
    businessProfile?.areFinancialFeaturesDisabled;
  const [isChoosingSku, setIsChoosingSku] = useState(false);
  const [isChoosingSkuToView, setIsChoosingSkuToView] = useState(false);
  const [skuToView, setSkuToView] = useState<InventorySKUListing | null>(null);

  const name = productGroupName(group);
  const totals = productStockTotals(group.skus, warehouseId);
  const imageSrc = productThumbnailSrc(group);
  const choosableSkus = skusToChooseFrom(group, warehouseId);
  const representativeSku = choosableSkus[0];
  const ownerName = showOwner ? group.skus[0]?.userBusinessName : null;
  const holdsOneSku = group.skuCount === 1 && choosableSkus.length === 1;

  const despatchSku = (sku: InventorySKUListing) => {
    navigate(
      `/despatch-note/create?inventorySkuId=${encodeURIComponent(
        sku.inventorySkuId
      )}&unit=${despatchUnitOf(sku)}`
    );
  };

  const handleDespatch = () => {
    const onlySku = despatchTargetOfProduct(group);
    if (onlySku) {
      despatchSku(onlySku);
      return;
    }
    if (!representativeSku) return;
    setIsChoosingSku(true);
  };

  const handleViewSkuInfo = () => {
    if (!representativeSku) return;
    if (holdsOneSku) {
      setSkuToView(representativeSku);
      return;
    }
    setIsChoosingSkuToView(true);
  };

  const productCell = (value: number, emphasisClassName = 'text-gray-900') => (
    <span className={`text-sm font-semibold ${emphasisClassName}`}>
      {numberToLocale(value)}
    </span>
  );

  return (
    <>
      <tr className='bg-gray-50/70'>
        <td className='px-4 py-3'>
          <div className='flex items-center gap-3'>
            <div className='flex shrink-0 items-center gap-1.5'>
              <EntityTile name={`${name} product`} imageSrc={imageSrc} />
              <EntityTile
                name={`${representativeSku?.name ?? name} SKU`}
                imageSrc={
                  representativeSku
                    ? skuOwnThumbnailSrc(representativeSku)
                    : null
                }
                size='sm'
              />
            </div>
            <div className='min-w-0'>
              {representativeSku ? (
                <button
                  type='button'
                  onClick={handleViewSkuInfo}
                  title='View SKU details'
                  className='truncate text-left text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline'
                >
                  {name}
                </button>
              ) : (
                <p className='truncate text-sm font-semibold text-gray-900'>
                  {name}
                </p>
              )}
              <p className='truncate text-xs text-gray-500'>
                {[
                  `${numberToLocale(group.skuCount)} SKU${
                    group.skuCount === 1 ? '' : 's'
                  }`,
                  totals.looseProducts > 0
                    ? `includes ${numberToLocale(totals.looseProducts)} loose`
                    : null,
                  ownerName ? `Shipper: ${ownerName}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>
        </td>
        <td className='px-4 py-3 text-sm text-gray-600'>
          {group.product?.productId ?? '—'}
        </td>
        <td className='text-center px-4 py-3'>
          {productCell(totals.inTransitProducts)}
        </td>
        <td className='text-center px-4 py-3 bg-green-50'>
          {productCell(totals.availableProducts, 'text-green-700')}
        </td>
        {isWarehouseClient(user) && (
          <td className='text-center px-4 py-3'>
            <Button
              label='Despatch'
              intent='secondary'
              size='sm'
              disabled={!canCreateDespatchNote}
              title={
                canCreateDespatchNote
                  ? 'Create Despatch Note'
                  : 'A bank account must be connected before you can create a despatch note.'
              }
              onClick={(e) => {
                e.stopPropagation();
                handleDespatch();
              }}
            />
          </td>
        )}
        <td className='text-center px-4 py-3'>
          {productCell(totals.allocatedProducts)}
        </td>
        <td className='text-center px-4 py-3 bg-blue-50'>
          {productCell(totals.outForDeliveryProducts, 'text-blue-700')}
        </td>
        <td className='text-center px-4 py-3'>
          {productCell(totals.blockedProducts)}
        </td>
        <td className='text-center px-4 py-3 bg-blue-50'>
          {productCell(totals.totalProducts)}
        </td>
        <td className='text-center px-4 py-3 bg-amber-50'>
          <span className='inline-flex flex-col items-center leading-tight'>
            {productCell(
              totals.lostProducts,
              totals.lostProducts > 0 ? 'text-amber-700' : 'text-gray-900'
            )}
            {totals.surplusProducts > 0 && (
              <span className='text-xs font-normal text-gray-600'>
                · {numberToLocale(totals.surplusProducts)} surplus
              </span>
            )}
          </span>
        </td>
      </tr>
      <ChooseSkuModal
        group={group}
        warehouseId={warehouseId}
        isOpen={isChoosingSku}
        setIsOpen={setIsChoosingSku}
        onSelectSku={(sku) => {
          setIsChoosingSku(false);
          despatchSku(sku);
        }}
      />
      <ChooseSkuModal
        group={group}
        warehouseId={warehouseId}
        isOpen={isChoosingSkuToView}
        setIsOpen={setIsChoosingSkuToView}
        title='Choose a SKU to view'
        action='view'
        onSelectSku={(sku) => {
          setIsChoosingSkuToView(false);
          setSkuToView(sku);
        }}
      />
      {skuToView && (
        <SkuInfoModal
          sku={skuToView}
          isOpen
          onClose={() => setSkuToView(null)}
        />
      )}
    </>
  );
}
